import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetChecks, useCreateFeatureCheckConfig } from "@/resources/validator-registry";
import { useGetAiPrompts } from "@/resources/ai-prompt";
import type { RegistryCheck } from "@/types/validator-registry";
import { SeveritySelect } from "./SeveritySelect";
import { resolveFeatureCategoryKey, resolveFeatureCategoryLabel } from "./constants";

type AddCheckToPromptModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  check: RegistryCheck;
};

/**
 * Scopes an existing validator check onto another generation prompt --
 * creates one `feature_check_config` row. Mirrors the prototype's two-step
 * Feature -> Prompt picker: `feature_name` values are grouped by category
 * (Feature) via `resolveFeatureCategoryKey`, then listed within that group by
 * their real `AiPrompt.label` (Prompt) when one exists, falling back to the
 * raw `feature_name` for the one orphan (`brand-signals`) with no matching
 * prompt row. Offer-able prompts are the deduped set of every `feature_name`
 * seen anywhere in the registry, minus the ones already scoped to this check
 * (data-driven, not a hardcoded list).
 */
export function AddCheckToPromptModal({ open, onOpenChange, check }: AddCheckToPromptModalProps) {
  const { data: checks } = useGetChecks();
  const { data: aiPrompts } = useGetAiPrompts();
  const createConfig = useCreateFeatureCheckConfig();

  const offerableFeatureNames = useMemo(() => {
    const scoped = new Set(check.feature_configs.map((fc) => fc.feature_name));
    // Only offer feature_names backed by a real generation prompt — a check can't
    // meaningfully be scoped onto a prompt that doesn't exist. This excludes
    // registry orphans (e.g. the legacy `brand-signals` mapping with no AiPrompt).
    const realPrompts = new Set((aiPrompts ?? []).map((p) => p.feature_key));
    const all = new Set<string>();
    (checks ?? []).forEach((c) => c.feature_configs.forEach((fc) => all.add(fc.feature_name)));
    return Array.from(all)
      .filter((name) => !scoped.has(name) && realPrompts.has(name))
      .sort();
  }, [checks, check.feature_configs, aiPrompts]);

  /** Offer-able feature_names grouped into their category (Feature). */
  const featureGroups = useMemo(() => {
    const groups = new Map<string, { key: string; label: string; prompts: { value: string; label: string }[] }>();
    offerableFeatureNames.forEach((featureName) => {
      const key = resolveFeatureCategoryKey(featureName);
      const label = resolveFeatureCategoryLabel(featureName);
      const promptLabel = aiPrompts?.find((p) => p.feature_key === featureName)?.label ?? featureName;
      const group = groups.get(key) ?? { key, label, prompts: [] };
      group.prompts.push({ value: featureName, label: promptLabel });
      groups.set(key, group);
    });
    return Array.from(groups.values());
  }, [offerableFeatureNames, aiPrompts]);

  const [featureKey, setFeatureKey] = useState("");
  const [featureName, setFeatureName] = useState("");
  const [severity, setSeverity] = useState<"hard_fail" | "warning">("hard_fail");

  const promptOptions = useMemo(
    () => featureGroups.find((g) => g.key === featureKey)?.prompts ?? [],
    [featureGroups, featureKey]
  );

  const selectedFeatureName = promptOptions.some((p) => p.value === featureName) ? featureName : "";

  const reset = () => {
    setFeatureKey("");
    setFeatureName("");
    setSeverity("hard_fail");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!selectedFeatureName) return;
    await createConfig.mutateAsync({
      feature_name: selectedFeatureName,
      check_id: check.id,
      severity,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-card" data-testid={`add-check-modal-${check.id}`}>
        <DialogHeader>
          <DialogTitle>Apply {check.label} to another prompt</DialogTitle>
          <DialogDescription>
            The shared parent rule applies immediately. Add a refinement afterwards if this prompt
            needs extra nuance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-muted-foreground">Feature</Label>
            <Select
              value={featureKey}
              onValueChange={(v) => {
                setFeatureKey(v);
                setFeatureName("");
              }}
            >
              <SelectTrigger
                className="bg-card"
                aria-label="Feature to apply this check to"
                data-testid={`add-check-feature-select-${check.id}`}
              >
                <SelectValue placeholder={featureGroups.length > 0 ? "Select a feature" : "No unscoped prompts left"} />
              </SelectTrigger>
              <SelectContent className="bg-popover border border-border">
                {featureGroups.map((g) => (
                  <SelectItem key={g.key} value={g.key}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-muted-foreground">Prompt</Label>
            <Select value={selectedFeatureName} onValueChange={setFeatureName} disabled={!featureKey}>
              <SelectTrigger
                className="bg-card"
                aria-label="Prompt to apply this check to"
                data-testid={`add-check-prompt-select-${check.id}`}
              >
                <SelectValue placeholder={featureKey ? "Select a prompt" : "Select a feature first"} />
              </SelectTrigger>
              <SelectContent className="bg-popover border border-border">
                {promptOptions.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-muted-foreground">Severity</Label>
            <SeveritySelect
              value={severity}
              onChange={setSeverity}
              ariaLabel="Severity for the new application"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!selectedFeatureName || createConfig.isPending}
            onClick={handleSubmit}
            data-testid={`add-check-submit-${check.id}`}
          >
            Apply check
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
